/**
 * Saved Comparisons API Client — Epic 6
 * Follows brokerApi.ts pattern for API calls
 */

import { authenticatedFetch } from './authApi'

const GATEWAY_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
  ? ''
  : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')

function authHeaders(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    const detail = err.detail
    const message =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join('; ') || `HTTP ${res.status}`
          : `HTTP ${res.status}`
    throw new Error(message)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SavedComparison {
  id: string
  name: string
  criteria: Record<string, unknown>
  results?: unknown[]
  notes?: string
  created_at: string
  updated_at: string
}

export interface SaveComparisonRequest {
  name: string
  criteria: Record<string, unknown>
  results: unknown[]
  notes?: string
}

// ── Saved Comparisons CRUD ────────────────────────────────────────────────────

export async function saveComparison(
  token: string,
  data: SaveComparisonRequest,
): Promise<SavedComparison> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/loans/comparisons/save`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })
  return handleResponse<SavedComparison>(res)
}

export async function getSavedComparisons(token: string): Promise<SavedComparison[]> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/loans/comparisons/saved`, {
    headers: authHeaders(token),
  })
  const data = await handleResponse<{ comparisons: SavedComparison[]; count: number }>(res)
  return Array.isArray(data.comparisons) ? data.comparisons : []
}

export async function getSavedComparison(
  token: string,
  id: string,
): Promise<SavedComparison> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/loans/comparisons/saved/${id}`, {
    headers: authHeaders(token),
  })
  const data = await handleResponse<{ comparison: SavedComparison }>(res)
  return data.comparison
}

export async function deleteSavedComparison(token: string, id: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/loans/comparisons/saved/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}
